import type { ReactNode } from 'react';

import { LuCheck } from 'react-icons/lu';

import { cn } from '../../../../utils/cn';
import type { ColorClasses } from '../../../../utils/Colors';

interface OptionCheckmarkProps {
    multiple: boolean;
    selected: boolean;
    optionColorClasses: ColorClasses | null;
}

const OptionCheckmark: React.FC<OptionCheckmarkProps> = ({ multiple, optionColorClasses, selected }) => (
    <div className="shrink-0 size-4 flex items-center justify-center">
        {multiple ? (
            <div
                className={cn(
                    'flex items-center justify-center',
                    'size-4',
                    'border rounded-[2px]',
                    optionColorClasses ? optionColorClasses.border : 'border-midnight',
                    selected && (optionColorClasses ? optionColorClasses.bg : 'bg-midnight'),
                )}
            >
                {selected && (
                    <LuCheck
                        data-testid="dropdown-check-icon"
                        className={cn('size-4', optionColorClasses ? optionColorClasses.text : 'text-white')}
                    />
                )}
            </div>
        ) : (
            selected && (
                <LuCheck
                    data-testid="dropdown-check-icon"
                    className={cn('size-4', optionColorClasses ? optionColorClasses.text : 'text-midnight')}
                />
            )
        )}
    </div>
);

interface PanelOptionProps {
    label: ReactNode;
    description?: ReactNode;
    selected: boolean;
    disabled?: boolean;
    multiple: boolean;
    hideCheckmarks: boolean;
    optionAlign: 'left' | 'center';
    optionClassName?: string;
    optionElementType: 'button' | 'div';
    optionColorClasses: ColorClasses | null;
    onSelect: () => void;
}

export const PanelOption: React.FC<PanelOptionProps> = ({
    description,
    disabled,
    hideCheckmarks,
    label,
    multiple,
    onSelect,
    optionAlign,
    optionClassName,
    optionColorClasses,
    optionElementType,
    selected,
}) => {
    const Element = optionElementType === 'div' ? 'div' : 'button';

    return (
        <Element
            {...(optionElementType === 'button' ? { disabled, type: 'button' } : {})}
            onClick={() => !disabled && onSelect()}
            className={cn(
                'flex items-center gap-2',
                description ? 'min-h-8' : 'h-8',
                'px-2 mx-1',
                'rounded-sm',
                'font-normal font-roboto text-sm leading-[1.46]',
                optionColorClasses ? optionColorClasses.text : 'text-midnight',
                optionColorClasses && optionColorClasses.bg,
                'text-left',
                'transition-colors',
                !disabled &&
                    !selected &&
                    (optionColorClasses ? optionColorClasses.hover : 'hover:bg-grey-outline-light'),
                selected && !optionColorClasses && 'bg-grey-outline-light',
                disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                optionElementType === 'div' && disabled && 'pointer-events-none',
                optionClassName,
            )}
        >
            {!hideCheckmarks && (
                <OptionCheckmark multiple={multiple} selected={selected} optionColorClasses={optionColorClasses} />
            )}
            <div className={cn('flex-1 min-w-0', optionAlign === 'center' ? 'text-center' : 'text-left')}>
                <div className="overflow-hidden text-ellipsis whitespace-nowrap">{label}</div>
                {description && (
                    <div className="text-xs opacity-80 overflow-hidden text-ellipsis whitespace-nowrap">
                        {description}
                    </div>
                )}
            </div>
        </Element>
    );
};

PanelOption.displayName = 'PanelOption';
